import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Alert,
  LinearProgress,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  PhotoCamera,
  CloudUploadOutlined,
  DeleteOutlined,
  ArrowBackOutlined,
  CheckCircleOutline,
  RefreshOutlined,
  Close,
  NavigateBefore,
  NavigateNext,
  PlayArrow,
  Pause,
} from '@mui/icons-material';
import { useQuoteStore } from '../stores/useQuoteStore';
import { useClientStore } from '../stores/useClientStore';
import fileService from '../services/fileService';
import { useNotification } from '../contexts/NotificationContext';

interface PhotoPreview {
  id: string;
  file?: File;
  preview: string;
  uploaded: boolean;
  uploading: boolean;
  error?: string;
  serverPhotoId?: number;
  blob?: string; // Blob URL pour les images chargées via fetch
}

const PhotosChantierPage: React.FC = () => {
  const navigate = useNavigate();
  const { devisId, projetId } = useParams<{ devisId?: string; projetId?: string }>();
  const { getDevisById } = useQuoteStore();
  const { getClientById } = useClientStore();
  const { showError } = useNotification();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [isLoadingDevis, setIsLoadingDevis] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isSlideshow, setIsSlideshow] = useState(false);
  const slideshowIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const devis = devisId ? getDevisById(parseInt(devisId, 10)) : null;
  const client = devis ? getClientById(devis.clientId) : null;

  // Déterminer le projetId selon le mode (devisId ou projetId direct)
  const actualProjetId = projetId ? parseInt(projetId, 10) : (devis?.projetId || null);

  // Vérifier si le devis est chargé
  useEffect(() => {
    if (devisId || projetId) {
      const timer = setTimeout(() => {
        setIsLoadingDevis(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [devisId, projetId]);

  // Fonction pour charger une image avec authentification
  const loadImageWithAuth = async (url: string): Promise<string> => {
    try {
      // Les URLs présignées ne nécessitent PAS de header Authorization
      // Elles contiennent déjà les informations d'authentification dans l'URL
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Convertir en blob puis en URL locale
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      console.log('✅ Blob URL créée avec succès');
      return blobUrl;
    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'image:', error);
      throw error;
    }
  };

  // Charger les photos existantes depuis le backend
  const loadPhotos = async () => {
    if (!actualProjetId) return;

    setIsLoadingPhotos(true);
    try {
      if (!actualProjetId) {
        console.warn('⚠️ Aucun projet associé');
        setIsLoadingPhotos(false);
        return;
      }

      console.log('📷 Chargement des photos pour projetId:', actualProjetId);

      const serverPhotos = await fileService.getFilesByEntity('PHOTO_CHANTIER', actualProjetId);
      console.log('📷 Photos récupérées du serveur:', serverPhotos);

      const photoPreviews: PhotoPreview[] = await Promise.all(
        serverPhotos.map(async (photo) => {
          try {
            // Récupérer l'URL présignée pour chaque photo
            const presignedUrlResponse = await fileService.getPresignedUrl(photo.id);
            console.log(`✅ URL présignée pour photo ${photo.id}:`, presignedUrlResponse);

            // Essayer de charger l'image via fetch pour créer un blob URL
            try {
              const blobUrl = await loadImageWithAuth(presignedUrlResponse.url);
              console.log(`✅ Blob URL créée pour photo ${photo.id}`);

              return {
                id: `server_${photo.id}`,
                preview: blobUrl,
                uploaded: true,
                uploading: false,
                serverPhotoId: photo.id,
                blob: blobUrl,
              };
            } catch (fetchError) {
              // Si le fetch échoue, utiliser directement l'URL présignée
              console.log(`⚠️ Fetch échoué pour photo ${photo.id}, utilisation directe de l'URL présignée`);
              return {
                id: `server_${photo.id}`,
                preview: presignedUrlResponse.url,
                uploaded: true,
                uploading: false,
                serverPhotoId: photo.id,
              };
            }
          } catch (error) {
            console.error(`❌ Erreur lors du chargement de la photo ${photo.id}:`, error);
            // En cas d'erreur, essayer d'utiliser urlS3 ou cheminLocal
            return {
              id: `server_${photo.id}`,
              preview: (photo as any).urlS3 || (photo as any).cheminLocal || '',
              uploaded: true,
              uploading: false,
              serverPhotoId: photo.id,
              error: 'Erreur de chargement',
            };
          }
        })
      );

      console.log('✅ Photos chargées avec URLs:', photoPreviews);
      setPhotos(photoPreviews);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des photos:', error);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  useEffect(() => {
    if (devis?.projetId) {
      loadPhotos();
    }
  }, [devis?.projetId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newPhotos: PhotoPreview[] = Array.from(files).map((file) => ({
      id: `local_${Date.now()}_${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      uploaded: false,
      uploading: false,
    }));

    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const handleRemovePhoto = async (photoId: string) => {
    const photo = photos.find((p) => p.id === photoId);

    if (photo?.serverPhotoId) {
      // Photo déjà uploadée, supprimer du serveur
      try {
        await fileService.deleteFile(photo.serverPhotoId);
        setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        showError('Erreur lors de la suppression de la photo');
      }
    } else {
      // Photo locale, juste supprimer du state
      setPhotos((prev) => {
        if (photo?.file) {
          URL.revokeObjectURL(photo.preview);
        }
        return prev.filter((p) => p.id !== photoId);
      });
    }
  };

  const handleUploadPhoto = async (photoId: string) => {
    const photo = photos.find((p) => p.id === photoId);
    if (!photo || !photo.file || !actualProjetId) return;

    setPhotos((prev) =>
      prev.map((p) =>
        p.id === photoId ? { ...p, uploading: true, error: undefined } : p
      )
    );

    try {
      // Upload avec progression - utiliser projetId (requis par le backend)
      const uploadedFile = await fileService.uploadFile(
        photo.file,
        'PHOTO_CHANTIER',
        actualProjetId,
        {
          onProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setUploadProgress(percentCompleted);
          }
        }
      );

      // Récupérer l'URL présignée pour affichage
      const presignedUrlResponse = await fileService.getPresignedUrl(uploadedFile.id);

      // Charger l'image avec authentification
      let displayUrl = presignedUrlResponse.url;
      try {
        const blobUrl = await loadImageWithAuth(presignedUrlResponse.url);
        displayUrl = blobUrl;
      } catch (fetchError) {
        console.log('⚠️ Fetch échoué après upload, utilisation directe de l\'URL présignée');
      }

      setPhotos((prev) =>
        prev.map((p) =>
          p.id === photoId
            ? {
                ...p,
                uploading: false,
                uploaded: true,
                serverPhotoId: uploadedFile.id,
                preview: displayUrl,
                blob: displayUrl,
              }
            : p
        )
      );

      console.log('Photo uploadée avec succès:', uploadedFile);
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === photoId
            ? {
                ...p,
                uploading: false,
                error: 'Erreur lors de l\'upload',
              }
            : p
        )
      );
    }
  };

  const handleUploadAll = async () => {
    const photosToUpload = photos.filter((p) => !p.uploaded && !p.uploading);

    for (const photo of photosToUpload) {
      await handleUploadPhoto(photo.id);
    }
  };

  // Ouvrir la galerie photo
  const handleOpenViewer = (index: number) => {
    setCurrentPhotoIndex(index);
    setViewerOpen(true);
  };

  // Fermer la galerie photo
  const handleCloseViewer = () => {
    setViewerOpen(false);
    if (isSlideshow) {
      toggleSlideshow();
    }
  };

  // Navigation vers la photo suivante
  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  // Navigation vers la photo précédente
  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  // Basculer le mode diaporama
  const toggleSlideshow = () => {
    if (isSlideshow) {
      // Arrêter le diaporama
      if (slideshowIntervalRef.current) {
        clearInterval(slideshowIntervalRef.current);
        slideshowIntervalRef.current = null;
      }
      setIsSlideshow(false);
    } else {
      // Démarrer le diaporama
      setIsSlideshow(true);
      slideshowIntervalRef.current = setInterval(() => {
        setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
      }, 3000); // Change de photo toutes les 3 secondes
    }
  };

  // Nettoyer l'intervalle du diaporama lors du démontage du composant
  useEffect(() => {
    return () => {
      if (slideshowIntervalRef.current) {
        clearInterval(slideshowIntervalRef.current);
      }
    };
  }, []);

  // Gérer les touches du clavier pour la navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!viewerOpen) return;

      switch (event.key) {
        case 'ArrowLeft':
          handlePrevPhoto();
          break;
        case 'ArrowRight':
          handleNextPhoto();
          break;
        case 'Escape':
          handleCloseViewer();
          break;
        case ' ':
          event.preventDefault();
          toggleSlideshow();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewerOpen, photos.length]);

  // Afficher un loader pendant le chargement initial
  if (isLoadingDevis) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Afficher une erreur seulement si le devis n'existe vraiment pas après le chargement
  if (!devis || !client) {
    return (
      <Box>
        <Alert severity="error">Devis ou client non trouvé</Alert>
        <Button onClick={() => navigate('/devis')} sx={{ mt: 2 }}>
          Retour aux devis
        </Button>
      </Box>
    );
  }

  const uploadedCount = photos.filter((p) => p.uploaded).length;
  const totalCount = photos.length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
          📸 Photos de chantier
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<RefreshOutlined />}
            onClick={loadPhotos}
            variant="outlined"
            disabled={isLoadingPhotos}
          >
            Actualiser
          </Button>
          <Button
            startIcon={<ArrowBackOutlined />}
            onClick={() => navigate(projetId ? `/projets/${projetId}` : `/devis/${devisId}`)}
            variant="outlined"
          >
            Retour
          </Button>
        </Box>
      </Box>

      {/* Informations du devis */}
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2, bgcolor: '#f5f5f5' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Devis: <strong>{devis.numero}</strong>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Client: <strong>{client.prenom} {client.nom}</strong>
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Zone d'upload */}
      <Paper sx={{ p: 4, mb: 3, boxShadow: 2, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 3 }}>
          Ajouter des photos
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PhotoCamera />}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              px: 4,
              py: 2,
              bgcolor: '#F59E0B',
              '&:hover': { bgcolor: '#D97706' },
              fontSize: '1.1rem',
            }}
          >
            Prendre / Choisir des photos
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
            Sur mobile, vous pouvez utiliser l'appareil photo directement
          </Typography>
        </Box>

        {photos.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1">
                  {totalCount} photo{totalCount > 1 ? 's' : ''}
                </Typography>
                {uploadedCount > 0 && (
                  <Chip
                    icon={<CheckCircleOutline />}
                    label={`${uploadedCount} uploadée${uploadedCount > 1 ? 's' : ''}`}
                    color="success"
                    size="small"
                  />
                )}
              </Box>
              {photos.some((p) => !p.uploaded) && (
                <Button
                  variant="contained"
                  startIcon={<CloudUploadOutlined />}
                  onClick={handleUploadAll}
                  sx={{
                    bgcolor: '#10B981',
                    '&:hover': { bgcolor: '#059669' },
                  }}
                >
                  Tout uploader
                </Button>
              )}
            </Box>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  Upload en cours... {uploadProgress}%
                </Typography>
              </Box>
            )}

            <Grid container spacing={2}>
              {photos.map((photo, index) => (
                <Grid item xs={12} sm={6} md={4} key={photo.id}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="200"
                      image={photo.preview}
                      alt="Photo chantier"
                      sx={{
                        objectFit: 'cover',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                        backgroundColor: '#f0f0f0',
                      }}
                      onClick={() => handleOpenViewer(index)}
                      onError={(e) => {
                        console.error('❌ Erreur de chargement image:', photo.preview);
                        (e.target as HTMLImageElement).style.backgroundColor = '#ffcccc';
                      }}
                      onLoad={() => {
                        console.log('✅ Image chargée avec succès:', photo.preview);
                      }}
                    />
                    <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                      <Box>
                        {photo.uploading && <CircularProgress size={24} />}
                        {photo.uploaded && (
                          <Chip
                            icon={<CheckCircleOutline />}
                            label="Uploadée"
                            color="success"
                            size="small"
                          />
                        )}
                        {photo.error && (
                          <Chip label={photo.error} color="error" size="small" />
                        )}
                      </Box>
                      <Box>
                        {!photo.uploaded && !photo.uploading && (
                          <Button
                            size="small"
                            startIcon={<CloudUploadOutlined />}
                            onClick={() => handleUploadPhoto(photo.id)}
                          >
                            Upload
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemovePhoto(photo.id)}
                          disabled={photo.uploading}
                        >
                          <DeleteOutlined />
                        </IconButton>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {photos.length === 0 && !isLoadingPhotos && (
          <Alert severity="info">
            Aucune photo. Cliquez sur le bouton ci-dessus pour ajouter des photos.
          </Alert>
        )}

        {isLoadingPhotos && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </Paper>

      {/* Galerie photo en plein écran */}
      <Dialog
        open={viewerOpen}
        onClose={handleCloseViewer}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            boxShadow: 'none',
            m: 0,
            maxWidth: '100vw',
            height: '100vh',
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Bouton fermer */}
          <IconButton
            onClick={handleCloseViewer}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 10,
            }}
          >
            <Close />
          </IconButton>

          {/* Compteur de photos */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              py: 1,
              borderRadius: 2,
              zIndex: 10,
            }}
          >
            <Typography variant="body2">
              {currentPhotoIndex + 1} / {photos.length}
            </Typography>
          </Box>

          {/* Bouton diaporama */}
          <IconButton
            onClick={toggleSlideshow}
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              bgcolor: isSlideshow ? 'rgba(102, 126, 234, 0.8)' : 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: isSlideshow ? 'rgba(102, 126, 234, 1)' : 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 10,
            }}
          >
            {isSlideshow ? <Pause /> : <PlayArrow />}
          </IconButton>

          {/* Bouton précédent */}
          <IconButton
            onClick={handlePrevPhoto}
            sx={{
              position: 'absolute',
              left: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <NavigateBefore fontSize="large" />
          </IconButton>

          {/* Image */}
          <Box
            component="img"
            src={photos[currentPhotoIndex]?.preview}
            alt={`Photo ${currentPhotoIndex + 1}`}
            sx={{
              maxWidth: '90%',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
          />

          {/* Bouton suivant */}
          <IconButton
            onClick={handleNextPhoto}
            sx={{
              position: 'absolute',
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <NavigateNext fontSize="large" />
          </IconButton>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PhotosChantierPage;
